import { WEB_PAGES, type WebPage } from '@praxadon/shared/pages';
import { Toaster, TooltipProvider } from '@praxadon/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, Suspense, lazy } from 'react';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useRouteError,
} from 'react-router';

import { ApiError } from '../api/client.ts';
import { LoginPage } from '../pages/auth/LoginPage.tsx';
import { ForgotPage, ResetPage } from '../pages/auth/PasswordResetPages.tsx';
import { InvitePage } from '../pages/auth/InvitePage.tsx';
import { SignupPage } from '../pages/auth/SignupPage.tsx';
import { SupportClaim } from '../pages/auth/SupportClaim.tsx';
import { Announcement, SupportBar, SupportRequest } from './Banners.tsx';
import { SessionProvider, useSession } from './session.tsx';

// The IDE carries Monaco (several megabytes); sign-in pages must not wait for it.
const IdePage = lazy(() => import('../ide/IdePage.tsx'));
// The class console: an instructor's page, and no reason for a student to load it.
const ConsolePage = lazy(() =>
  import('../pages/console/ConsolePage.tsx').then((module) => ({ default: module.ConsolePage })),
);
// The platform console: ours, on the platform host, and in nobody else's bundle.
const PlatformApp = lazy(() =>
  import('../pages/platform/PlatformApp.tsx').then((module) => ({ default: module.PlatformApp })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Sign-in state and permissions change underneath us (a ban, a lock); do not
      // keep retrying a 401/403 or serving a stale copy for long.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

/** Anything behind sign-in: send the student to finish signing in, then come back. */
function RequireSession({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const location = useLocation();
  if (!session?.authenticated || session.stage !== 'active') {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`${WEB_PAGES.login}?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

function FullPageMessage({ title, body }: { title: string; body: string }) {
  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-fg">{title}</h1>
        <p className="mt-2 text-sm text-fg-muted">{body}</p>
        <a href="/" className="mt-4 inline-block text-sm text-fg underline">
          Go to your projects
        </a>
      </div>
    </main>
  );
}

function RouteError() {
  const error = useRouteError();
  if (import.meta.env.DEV) console.error(error);
  return (
    <FullPageMessage
      title="Something went wrong"
      body="This page hit a problem it couldn't recover from. Reloading usually fixes it — your saved work is safe."
    />
  );
}

/**
 * The pages other things link to (emails above all), from the table the API builds its
 * links from. Typed as a complete record: a page added there without one here does not
 * compile, so no emailed link can lead to "page not found".
 */
const LINKED_PAGES: Record<WebPage, ReactNode> = {
  login: <LoginPage />,
  signup: <SignupPage />,
  acceptInvite: <InvitePage />,
  forgotPassword: <ForgotPage />,
  resetPassword: <ResetPage />,
  classConsole: (
    <RequireSession>
      <ConsolePage />
    </RequireSession>
  ),
};

const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      ...Object.entries(LINKED_PAGES).map(([page, element]) => ({
        path: WEB_PAGES[page as WebPage],
        element,
      })),
      {
        path: '/',
        element: (
          <RequireSession>
            <IdePage />
          </RequireSession>
        ),
      },
      {
        // Where a one-time support link lands, to be spent for a session.
        path: '/support',
        element: <SupportClaim />,
      },
      {
        path: '/p/:projectId',
        element: (
          <RequireSession>
            <IdePage />
          </RequireSession>
        ),
      },
      {
        path: '*',
        element: (
          <FullPageMessage
            title="Page not found"
            body="There's nothing at this address. Check the link and try again."
          />
        ),
      },
    ],
  },
]);

/**
 * One app, two kinds of host.
 *
 * A class's own address gets the editor and everything around it. The platform address
 * — `ide.praxadon.com` itself — gets the platform console instead: it belongs to no
 * class, so none of the class routes would mean anything there.
 */
function Everything() {
  const { site } = useSession();
  return (
    <>
      <Announcement />
      {site.kind === 'tenant' ? (
        <>
          <SupportBar />
          <SupportRequest />
          <RouterProvider router={router} />
        </>
      ) : (
        <PlatformApp />
      )}
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <SessionProvider>
          <Suspense fallback={null}>
            <Everything />
          </Suspense>
        </SessionProvider>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
